import React from "react";
import ShadowPreview from "./ShadowPreview";
import { buildPrintHTML } from "../report/buildPrintHTML";
import { captureAllMapSnapshots } from "../utils/mapUtils";
import { expandPdfLegalDocs } from "../utils/pdfUtils";
import { api } from "../api";

export default function ReportSection({ collectState, getFileName, handleInstantPDF, handleDownloadHTML, handleSave, handleOpenClick, showToast, reportType, setReportType }) {
  const [html, setHtml] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const generate = React.useCallback(async () => {
    setLoading(true);
    showToast("⏳ Generating report preview…");
    try {
      const state = collectState();

      // Always fetch the latest letterhead from the company profile so the
      // super admin's uploaded PNG is used exactly as-is, without modification.
      try {
        const lhData = await api.getCompanyLetterhead();
        state.letterheadPng = lhData.letterhead_png || "";
        state.letterheadTextBox = lhData.letterhead_text_box || null;
        state.letterheadWatermarkBox = lhData.letterhead_watermark_box || null;
      } catch (_) {
        // keep whatever is already in state from the mount-time fetch
      }

      if ((state.reportType || "preliminary") === "final") {
        state.legalDocs = await expandPdfLegalDocs(state.legalDocs || []);
      }
      const mapSnapshots = await captureAllMapSnapshots(state.properties || []);
      const result = buildPrintHTML(state, getFileName("pdf"), false, mapSnapshots);
      setHtml(result);
    } finally {
      setLoading(false);
    }
  }, [collectState, getFileName, showToast]);

  // Auto-regenerate when report type changes (if a preview already exists)
  const prevTypeRef = React.useRef(reportType);
  React.useEffect(() => {
    if (prevTypeRef.current !== reportType) {
      prevTypeRef.current = reportType;
      if (html !== null) {
        generate();
      }
    }
  }, [reportType, html, generate]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>

      {/* Header row */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"10px"}}>
        <div>
          <h2 style={{fontSize:"18px",fontWeight:700,color:"var(--navy)",margin:0}}>📄 Report Preview</h2>
          <p style={{fontSize:"13px",color:"var(--text-3)",marginTop:"4px"}}>
            Select report type, then generate a live preview.
          </p>
        </div>
        <div style={{display:"flex",gap:"10px",flexWrap:"wrap"}}>
          <button
            onClick={generate}
            disabled={loading}
            style={{padding:"10px 20px",background:"var(--navy)",color:"#fff",border:"none",
              borderRadius:"var(--radius)",fontWeight:700,fontSize:"13px",cursor:loading?"not-allowed":"pointer",
              opacity:loading?0.7:1,display:"flex",alignItems:"center",gap:"6px"}}>
            {loading ? "⏳ Generating…" : "🔄 Generate Preview"}
          </button>
          <button
            onClick={handleInstantPDF}
            style={{padding:"10px 20px",background:"#c0392b",color:"#fff",border:"none",
              borderRadius:"var(--radius)",fontWeight:700,fontSize:"13px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:"6px"}}>
            ⬇️ Download PDF
          </button>
          <button
            onClick={handleDownloadHTML}
            style={{padding:"10px 20px",background:"#2d6a4f",color:"#fff",border:"none",
              borderRadius:"var(--radius)",fontWeight:700,fontSize:"13px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:"6px"}}>
            🌐 Download HTML
          </button>
        </div>
      </div>

      {/* Report type selector */}
      <div className="report-type-bar">
        <span className="report-type-label">Report Type:</span>
        <label className={`radio-btn-sm ${reportType==="preliminary"?"active":""}`}
          style={{cursor:"pointer"}}>
          <input type="radio" checked={reportType==="preliminary"}
            onChange={()=>setReportType("preliminary")} style={{width:"auto"}}/>
          Preliminary
        </label>
        <label className={`radio-btn-sm ${reportType==="final"?"active":""}`}
          style={{cursor:"pointer"}}>
          <input type="radio" checked={reportType==="final"}
            onChange={()=>setReportType("final")} style={{width:"auto"}}/>
          Final
        </label>
        <span className="filename-preview">📄 {getFileName("pdf")}</span>
      </div>

      {/* File actions */}
      <div className="action-grid" style={{gridTemplateColumns:"repeat(2,1fr)"}}>
        <button className="action-btn save" onClick={handleSave}>💾 Save File</button>
        <button className="action-btn open" onClick={handleOpenClick}>📂 Open File</button>
      </div>

      {/* Empty state */}
      {!html && !loading && (
        <div style={{textAlign:"center",padding:"60px 20px",background:"var(--surface)",
          borderRadius:"var(--radius-lg)",border:"2px dashed var(--border)",color:"var(--text-3)"}}>
          <div style={{fontSize:"48px",marginBottom:"12px"}}>📋</div>
          <p style={{fontSize:"15px",fontWeight:600,color:"var(--text-2)",marginBottom:"6px"}}>No preview yet</p>
          <p style={{fontSize:"13px"}}>Select a report type above and click <strong>Generate Preview</strong>.</p>
        </div>
      )}

      {/* Preview frame */}
      {html && (
        <div style={{border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",overflow:"hidden",
          height:"calc(100vh - 320px)",minHeight:"500px",display:"flex",flexDirection:"column"}}>
          <div style={{background:"var(--surface-3)",borderBottom:"1px solid var(--border)",
            padding:"8px 14px",fontSize:"12px",color:"var(--text-3)",display:"flex",
            alignItems:"center",justifyContent:"space-between"}}>
            <span>💡 Use <strong>Download HTML</strong> → open in browser → <kbd style={{background:"#eee",padding:"1px 5px",borderRadius:"3px",fontSize:"11px"}}>Ctrl+P</kbd> to save as PDF</span>
            <button onClick={generate} disabled={loading}
              style={{fontSize:"11px",padding:"3px 10px",background:"var(--navy)",color:"#fff",
                border:"none",borderRadius:"4px",cursor:"pointer",opacity:loading?0.6:1}}>
              {loading ? "…" : "↺ Refresh"}
            </button>
          </div>
          <ShadowPreview html={html} />
        </div>
      )}
    </div>
  );
}
